import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, Form, Header, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showSuccess } from '../helpers';
import { UserContext } from '../context/User';
import { isValidUsernameV108 } from '../helpers/usernamePolicyV108';

const emptyDraft = { username: '', display_name: '', password: '' };

export default function PersonalProfileV108({ bindingUpdate = {} }) {
  const { t } = useTranslation();
  const [userState, dispatch] = useContext(UserContext);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: result } = await API.get('/api/user/self');
      if (!result.success || !result.data) {
        throw new Error(result.message || t('user.edit.load_failed'));
      }
      setProfile(result.data);
      setDraft({
        username: result.data.username || '',
        display_name: result.data.display_name || '',
        password: '',
      });
    } catch (failure) {
      setError(failure?.message || t('user.edit.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!profile || loading || submitting.current) return;
    if (!isValidUsernameV108(draft.username)) {
      setError(t('organization.validation.username_length'));
      return;
    }
    submitting.current = true;
    setSaving(true);
    setError('');
    try {
      const { data: result } = await API.put('/api/user/self', draft);
      if (!result.success) {
        throw new Error(
          result.message || t('setting.personal.profile.save_failed')
        );
      }
      // Keep the session's existing permissions; never persist the password.
      const identity = {
        ...userState.user,
        username: draft.username,
        display_name: draft.display_name,
      };
      localStorage.setItem('user', JSON.stringify(identity));
      dispatch({ type: 'login', payload: identity });
      setDraft((current) => ({ ...current, password: '' }));
      showSuccess(t('user.messages.update_success'));
    } catch (failure) {
      setError(failure?.message || t('setting.personal.profile.save_failed'));
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  const change = (_, { name, value }) =>
    setDraft((current) => ({ ...current, [name]: value }));

  return (
    <section
      className='personal-profile-v108'
      aria-labelledby='personal-profile-title'
    >
      <Header as='h3' id='personal-profile-title'>
        {t('v9.profile')}
      </Header>
      {error && (
        <Message negative role='alert'>
          {error}
        </Message>
      )}
      {!loading && !profile ? (
        <Button
          icon='refresh'
          content={t('setting.personal.profile.retry')}
          onClick={load}
        />
      ) : (
        <Form loading={loading} onSubmit={save} autoComplete='off'>
          <div className='personal-profile-grid-v108'>
            <Form.Input
              id='profile-username'
              label={t('user.edit.username')}
              name='username'
              value={draft.username}
              placeholder={t('user.edit.username_placeholder')}
              error={
                Boolean(draft.username) &&
                !isValidUsernameV108(draft.username) && {
                  content: t('organization.validation.username_length'),
                }
              }
              onChange={change}
              autoComplete='username'
              required
              disabled={loading || saving}
            />
            <Form.Input
              id='profile-display-name'
              label={t('user.edit.display_name')}
              name='display_name'
              value={draft.display_name}
              onChange={change}
              disabled={loading || saving}
            />
            <Form.Input
              id='profile-password'
              label={t('user.edit.password')}
              name='password'
              type='password'
              value={draft.password}
              onChange={change}
              autoComplete='new-password'
              disabled={loading || saving}
              placeholder={t('setting.personal.profile.password_placeholder')}
            />
            <Form.Input
              id='profile-email'
              label={t('user.edit.email')}
              name='email'
              value={bindingUpdate.email ?? profile?.email ?? ''}
              readOnly
              placeholder={t('setting.personal.profile.not_linked')}
            />
            {['github_id', 'wechat_id']
              .filter((name) => profile?.[name])
              .map((name) => (
                <Form.Input
                  key={name}
                  id={`profile-${name}`}
                  name={name}
                  label={t(`user.edit.${name}`)}
                  value={profile[name]}
                  readOnly
                />
              ))}
          </div>
          <Button
            type='submit'
            primary
            icon='save'
            loading={saving}
            disabled={
              loading ||
              saving ||
              !profile ||
              !isValidUsernameV108(draft.username)
            }
            content={t('setting.personal.profile.save')}
          />
        </Form>
      )}
    </section>
  );
}
